import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TYPES = ['Wrong', 'Unclear', 'Useful', 'Idea'];
const MAX_COMMENT = 4000;

const THUMB = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M4.5 7.5l2.7-4.9c.3-.6 1.1-.8 1.7-.4.5.3.7.9.5 1.5l-.7 2.5h4.1c1 0 1.8.9 1.6 1.9l-.8 4.8c-.2.9-.9 1.6-1.8 1.6H4.5z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><rect x="1.5" y="7" width="3" height="7.5" rx=".8" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>';
const COMMENT = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M2.5 2.75c0-.69.56-1.25 1.25-1.25h8.5c.69 0 1.25.56 1.25 1.25v6.5c0 .69-.56 1.25-1.25 1.25H6.6L3.6 13.1a.6.6 0 0 1-1.1-.45z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M5.5 5h5M5.5 7.5h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

// Helpful, not helpful and comment buttons beside each heading in the report: shown while the heading's block
// (the chart or callout it titles) is hovered, and kept once voted on or while the open panel is about it.
// A vote is a navy fill (14:1); the panel's target block is outlined (3.8:1). The negative vertical margins stop
// the 26px buttons making headings taller than the report drew them.
const REPORT_CSS = `
    .portal-feedback-tools {
        display: inline-flex; align-items: center; gap: 4px; margin: -6px 0 -6px 8px;
        vertical-align: middle; opacity: 0; transition: opacity .15s;
    }
    .portal-feedback-block:hover .portal-feedback-tools, .portal-feedback-tools:focus-within,
    .portal-feedback-tools.has-vote, .portal-feedback-tools.has-feedback, .portal-feedback-tools.has-message,
    .portal-feedback-target .portal-feedback-tools { opacity: 1; }
    .portal-feedback-tools button {
        display: inline-flex; align-items: center; justify-content: center;
        width: 26px; height: 26px; margin: 0; padding: 0; line-height: 0; box-shadow: none;
        border: 1px solid #C9DCE8; border-radius: 99px; background: #fff; color: #0074B3; cursor: pointer; position: relative;
    }
    .portal-feedback-tools button:hover { border-color: #14a4dd; background: #E7F4FB; color: #002C4E; }
    .portal-feedback-tools button:focus-visible { outline: 2px solid #14a4dd; outline-offset: 2px; }
    .portal-feedback-tools button[aria-pressed="true"], .portal-feedback-tools.has-feedback .portal-feedback-comment {
        border-color: #002C4E; background: #002C4E; color: #fff;
    }
    .portal-feedback-tools button::after {
        content: attr(data-portal-tip); position: absolute; z-index: 20; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
        padding: 5px 9px; border-radius: 6px; background: #002C4E; color: #fff; box-shadow: 0 4px 12px rgba(0, 44, 78, .25);
        font-size: 12px; font-weight: 500; line-height: 1.3; letter-spacing: 0; text-transform: none; white-space: nowrap;
        pointer-events: none; opacity: 0; transition: opacity .12s;
    }
    .portal-feedback-tools button:hover::after, .portal-feedback-tools button:focus-visible::after { opacity: 1; transition-delay: .2s; }
    .portal-feedback-tools [data-vote="NotHelpful"] svg { transform: rotate(180deg); }
    .portal-feedback-tools::after { content: attr(data-message); margin-left: 2px; font-size: 12px; font-weight: 600; letter-spacing: 0; text-transform: none; color: #0074B3; }
    @media (hover: none) { .portal-feedback-tools { opacity: .7; } }
    .portal-feedback-target { outline: 2px solid #0E8CC3; outline-offset: 6px; border-radius: 8px; }
`;

const textOf = (element) => element.textContent.replace(/\s+/g, ' ').trim();

// The largest element round a heading that holds no other heading: the chart, callout or finding it titles
const blockOf = (heading, section) => {
    let block = heading;
    while (block.parentElement && block.parentElement !== section && block.parentElement.querySelectorAll('h4').length === 1) {
        block = block.parentElement;
    }
    return block;
};

// Floating feedback button and panel for the Advanced Insights tab. Feedback is tied to the section or heading
// being read when the panel opens, or to the heading whose icon was clicked.
export function AdvancedInsightsFeedback({ reportDoc, frameRef, siteGUID, sections }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [targets, setTargets] = useState([]);
    const [targetKey, setTargetKey] = useState('');
    const [type, setType] = useState(null);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState('idle');
    const [askWhy, setAskWhy] = useState(false);
    const [showAll, setShowAll] = useState(false);
    // The signed-in user's own votes and feedback on this site's reports
    const [mine, setMine] = useState({ votes: [], feedback: [] });
    const mineRef = useRef(mine);
    const openForRef = useRef(() => { });
    const voteRef = useRef(() => { });
    const paintRef = useRef(() => { });
    const commentRef = useRef(null);

    const target = targets.find((x) => x.key === targetKey);

    // Votes and the filled comment icon belong to the report version being read; a new version starts afresh
    const version = reportDoc ? new Date(reportDoc.lastModified) : null;
    const versionTime = version && !isNaN(version) ? version.getTime() : null;
    const isThisVersion = (reportVersion) => !!reportVersion && versionTime !== null && new Date(reportVersion).getTime() === versionTime;
    const keyOf = (sectionId, heading) => (sectionId || '') + '\n' + (heading || '');
    const updateMine = (change) => {
        mineRef.current = change(mineRef.current);
        setMine(mineRef.current);
    };

    const formatDay = (time) => new Date(time).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

    // Everything feedback can be about, in report order: each section, then the visible headings in it
    const readTargets = () => {
        const list = [];
        sections.forEach((section) => {
            const element = reportDoc.getElementById(section.id);
            if (!element) {
                return;
            }
            list.push({ key: section.id, sectionId: section.id, sectionTitle: section.label, heading: null, element: element, block: null });
            element.querySelectorAll('h4').forEach((headingElement, i) => {
                const heading = textOf(headingElement);
                if (heading && headingElement.getClientRects().length > 0) {
                    list.push({ key: section.id + '/' + i, sectionId: section.id, sectionTitle: section.label, heading: heading, element: headingElement, block: blockOf(headingElement, element) });
                }
            });
        });
        return list;
    };

    const postFeedback = (about, feedbackType, feedbackComment) => {
        return fetch('/api/AdvancedInsightsAPI/Feedback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                siteGUID: siteGUID,
                reportVersion: versionTime === null ? null : new Date(versionTime).toISOString(),
                sectionId: about ? about.sectionId : null,
                sectionTitle: about ? about.sectionTitle : null,
                heading: about ? about.heading : null,
                type: feedbackType,
                comment: feedbackComment
            })
        }).then((response) => {
            if (!response.ok) {
                throw new Error(response.statusText);
            }
        });
    };

    // Opens on the clicked heading; otherwise on the chart or callout across the middle of the window,
    // or failing that the whole section the middle of the window is in. After a not-helpful vote it asks why.
    openForRef.current = (heading, afterNotHelpful) => {
        const list = readTargets();
        const middle = window.innerHeight / 2 - frameRef.current.getBoundingClientRect().top;
        let chosen = heading ? list.find((x) => x.element === heading) : list.find((x) => {
            const box = x.block && x.block.getBoundingClientRect();
            return box && box.top <= middle && box.bottom >= middle;
        });
        if (!chosen && !heading) {
            list.forEach((x) => {
                if (!x.heading && x.element.getBoundingClientRect().top <= middle) {
                    chosen = x;
                }
            });
        }
        setTargets(list);
        setTargetKey(chosen ? chosen.key : '');
        setAskWhy(!!afterNotHelpful);
        setStatus((current) => (current === 'sent' ? 'idle' : current));
        setOpen(true);
    };

    // Shows a heading's buttons as the user left them: their vote and whether they've commented, on this version
    paintRef.current = (tools) => {
        const key = tools.dataset.key;
        const vote = mineRef.current.votes.find((v) => isThisVersion(v.reportVersion) && keyOf(v.sectionId, v.heading) === key);
        const commented = mineRef.current.feedback.some((f) => isThisVersion(f.reportVersion) && keyOf(f.sectionId, f.heading) === key);
        tools.dataset.voted = vote ? vote.type : '';
        tools.querySelectorAll('[data-vote]').forEach((b) => b.setAttribute('aria-pressed', String(!!vote && b.dataset.vote === vote.type)));
        tools.classList.toggle('has-vote', !!vote);
        tools.classList.toggle('has-feedback', commented);
        const commentButton = tools.querySelector('.portal-feedback-comment');
        const tip = t(commented ? 'components.advancedinsights.feedback.onthisagain' : 'components.advancedinsights.feedback.onthis');
        commentButton.dataset.portalTip = tip;
        commentButton.setAttribute('aria-label', tip);
    };

    // A short message beside the buttons. It is CSS content rather than text, so the heading's text (which
    // feedback records) never changes.
    const flash = (tools, message) => {
        tools.dataset.message = message;
        tools.classList.add('has-message');
        clearTimeout(tools.timer);
        tools.timer = setTimeout(() => {
            tools.dataset.message = '';
            tools.classList.remove('has-message');
        }, 2500);
    };

    // Records a vote straight away; voting again on the same heading changes it, and the admin page counts
    // each person's latest vote on each report version
    voteRef.current = (heading, vote) => {
        const tools = heading.querySelector(':scope > .portal-feedback-tools');
        const about = readTargets().find((x) => x.element === heading);
        const previous = tools.dataset.voted || '';
        if (!about || vote === previous) {
            return;
        }
        const setVote = (value) => updateMine((current) => {
            const others = current.votes.filter((v) => !(isThisVersion(v.reportVersion) && keyOf(v.sectionId, v.heading) === tools.dataset.key));
            const added = value ? [{ reportVersion: new Date(versionTime).toISOString(), sectionId: about.sectionId, heading: about.heading, type: value }] : [];
            return { ...current, votes: others.concat(added) };
        });
        setVote(vote);
        flash(tools, t('components.advancedinsights.feedback.thanksshort'));
        postFeedback(about, vote, null).catch(() => {
            setVote(previous);
            flash(tools, t('components.advancedinsights.feedback.voteerror'));
        });
        if (vote === 'NotHelpful') {
            openForRef.current(heading, true);
        }
    };

    useEffect(() => {
        fetch('/api/AdvancedInsightsAPI/MyFeedback?siteGUID=' + encodeURIComponent(siteGUID))
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => data && updateMine(() => ({ votes: data.votes || [], feedback: data.feedback || [] })))
            .catch(() => { });
    }, [siteGUID]);

    useEffect(() => {
        if (reportDoc) {
            reportDoc.querySelectorAll('.portal-feedback-tools').forEach((tools) => paintRef.current(tools));
        }
    }, [reportDoc, mine, versionTime]);

    useEffect(() => {
        if (!reportDoc || sections.length === 0) {
            return;
        }
        if (!reportDoc.getElementById('portal-feedback-style')) {
            const style = reportDoc.createElement('style');
            style.id = 'portal-feedback-style';
            style.textContent = REPORT_CSS;
            reportDoc.head.appendChild(style);
        }

        // Tooltips are CSS so they show at once, unlike the browser's delayed title tooltip. Not data-tip:
        // reports run their own tooltip on any element with it, which would show a second one.
        const addButton = (tools, label, icon, onClick) => {
            const button = reportDoc.createElement('button');
            button.type = 'button';
            button.dataset.portalTip = label;
            button.setAttribute('aria-label', label);
            button.innerHTML = icon;
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            });
            tools.appendChild(button);
            return button;
        };
        const addIcons = () => sections.forEach((section) => {
            const element = reportDoc.getElementById(section.id);
            (element ? element.querySelectorAll('h4') : []).forEach((heading) => {
                if (heading.querySelector(':scope > .portal-feedback-tools')) {
                    return;
                }
                blockOf(heading, element).classList.add('portal-feedback-block');
                const tools = reportDoc.createElement('span');
                tools.className = 'portal-feedback-tools';
                tools.dataset.key = keyOf(section.id, textOf(heading));
                ['Helpful', 'NotHelpful'].forEach((vote) => {
                    const button = addButton(tools, t('components.advancedinsights.feedback.' + vote.toLowerCase()), THUMB, () => voteRef.current(heading, vote));
                    button.dataset.vote = vote;
                    button.setAttribute('aria-pressed', 'false');
                });
                addButton(tools, t('components.advancedinsights.feedback.onthis'), COMMENT, () => openForRef.current(heading)).classList.add('portal-feedback-comment');
                heading.appendChild(tools);
                paintRef.current(tools);
            });
        });

        // The report redraws some blocks after load, which drops their icons
        let pending = 0;
        const observer = new MutationObserver(() => {
            if (!pending) {
                pending = requestAnimationFrame(() => {
                    pending = 0;
                    addIcons();
                });
            }
        });
        addIcons();
        observer.observe(reportDoc.body, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            cancelAnimationFrame(pending);
        };
    }, [reportDoc, sections, t]);

    // Outlines the chart or callout the panel is about, so it is clear what the feedback will be tied to
    useEffect(() => {
        const element = open && target ? target.block : null;
        if (element) {
            element.classList.add('portal-feedback-target');
            return () => element.classList.remove('portal-feedback-target');
        }
    }, [open, target]);

    useEffect(() => {
        if (open && commentRef.current) {
            commentRef.current.focus();
        }
    }, [open, targetKey]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const closeOnEscape = (e) => e.key === 'Escape' && setOpen(false);
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [open]);

    useEffect(() => {
        if (status === 'sent') {
            const timer = setTimeout(() => setOpen(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const send = (e) => {
        e.preventDefault();
        setStatus('sending');
        postFeedback(target, type, comment)
            .then(() => {
                const sent = {
                    time: new Date().toISOString(),
                    reportVersion: versionTime === null ? null : new Date(versionTime).toISOString(),
                    sectionId: target ? target.sectionId : null,
                    sectionTitle: target ? target.sectionTitle : null,
                    heading: target ? target.heading : null,
                    type: type,
                    comment: comment.trim() || null
                };
                updateMine((current) => ({ ...current, feedback: [sent].concat(current.feedback) }));
                setStatus('sent');
                setType(null);
                setComment('');
            })
            .catch(() => setStatus('error'));
    };

    if (!reportDoc || sections.length === 0) {
        return <></>;
    }

    // The user's earlier feedback on what the panel is about, from any report version
    const mineHere = mine.feedback.filter((f) => (f.sectionId || null) === (target ? target.sectionId : null) && (f.heading || null) === (target ? target.heading : null));
    const historyItem = (item, i, withAbout) => (
        <li key={i} className="ai-feedback-item">
            <div className="ai-feedback-item-meta">
                {formatDay(item.time)}
                {item.type ? ' · ' + t('components.advancedinsights.feedback.types.' + item.type.toLowerCase()) : ''}
                {item.reportVersion && !isThisVersion(item.reportVersion) ? ' · ' + t('components.advancedinsights.feedback.reportof', { date: formatDay(item.reportVersion) }) : ''}
            </div>
            {withAbout ? (
                <div className="ai-feedback-item-about">
                    {item.sectionTitle ? item.sectionTitle + (item.heading ? ' › ' + item.heading : '') : t('components.advancedinsights.feedback.wholereport')}
                </div>
            ) : (
                <></>
            )}
            {item.comment ? <div className="ai-feedback-item-comment">{item.comment}</div> : <></>}
        </li>
    );

    return (
        <>
            {open ? (
                <div className="ai-feedback-panel" role="dialog" aria-labelledby="ai-feedback-title">
                    <div className="ai-feedback-head">
                        <h2 id="ai-feedback-title">{t('components.advancedinsights.feedback.title')}</h2>
                        <button type="button" className="ai-feedback-close" onClick={() => setOpen(false)} aria-label={t('components.advancedinsights.feedback.close')}>
                            <i className="fal fa-times"></i>
                        </button>
                    </div>
                    {status === 'sent' ? (
                        <div className="ai-feedback-sent" role="status">
                            <i className="fad fa-check-circle"></i>
                            {t('components.advancedinsights.feedback.thanks')}
                        </div>
                    ) : (
                        <form onSubmit={send}>
                            <label htmlFor="ai-feedback-about">{t('components.advancedinsights.feedback.about')}</label>
                            <select id="ai-feedback-about" className="form-select form-select-sm" value={targetKey} onChange={(e) => setTargetKey(e.target.value)}>
                                <option value="">{t('components.advancedinsights.feedback.wholereport')}</option>
                                {sections.map((section) => (
                                    <optgroup key={section.id} label={section.label}>
                                        {targets.filter((x) => x.sectionId === section.id).map((x) => (
                                            <option key={x.key} value={x.key}>{x.heading || t('components.advancedinsights.feedback.wholesection')}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <div className="ai-feedback-label" id="ai-feedback-type">{t('components.advancedinsights.feedback.type')}</div>
                            <div className="ai-feedback-types" role="group" aria-labelledby="ai-feedback-type">
                                {TYPES.map((value) => (
                                    <button key={value} type="button" aria-pressed={type === value}
                                        className={'ai-feedback-type' + (type === value ? ' is-selected' : '')}
                                        onClick={() => setType(type === value ? null : value)}>
                                        {t('components.advancedinsights.feedback.types.' + value.toLowerCase())}
                                    </button>
                                ))}
                            </div>
                            <label htmlFor="ai-feedback-comment">{t('components.advancedinsights.feedback.comment')}</label>
                            <textarea id="ai-feedback-comment" ref={commentRef} className="form-control" rows={4} maxLength={MAX_COMMENT}
                                placeholder={t(askWhy ? 'components.advancedinsights.feedback.placeholdernothelpful' : 'components.advancedinsights.feedback.placeholder')}
                                value={comment} onChange={(e) => setComment(e.target.value)} />
                            {status === 'error' ? <div className="ai-feedback-error" role="alert">{t('components.advancedinsights.feedback.error')}</div> : <></>}
                            <div className="ai-feedback-actions">
                                <button type="submit" className="btn btn-primary btn-sm" disabled={status === 'sending' || (!type && !comment.trim())}>
                                    {t('components.advancedinsights.feedback.send')}
                                </button>
                            </div>
                        </form>
                    )}
                    {status !== 'sent' && mine.feedback.length > 0 ? (
                        <div className="ai-feedback-history">
                            {mineHere.length > 0 ? (
                                <>
                                    <div className="ai-feedback-label">{t('components.advancedinsights.feedback.historythis')}</div>
                                    <ul>{mineHere.map((item, i) => historyItem(item, i, false))}</ul>
                                </>
                            ) : (
                                <></>
                            )}
                            <button type="button" className="ai-feedback-link" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
                                {showAll ? t('components.advancedinsights.feedback.historyhide') : t('components.advancedinsights.feedback.historyall', { count: mine.feedback.length })}
                            </button>
                            {showAll ? <ul>{mine.feedback.map((item, i) => historyItem(item, i, true))}</ul> : <></>}
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            ) : (
                <></>
            )}
            <button type="button" className="ai-feedback-button" aria-expanded={open}
                onClick={() => (open ? setOpen(false) : openForRef.current(null))}>
                <i className="fad fa-comment-alt-lines"></i>
                {t('components.advancedinsights.feedback.button')}
            </button>
        </>
    );
}
